/**
 * OPO — Cornelius entity graph (Tier-3 B3).
 */
import React, { useState, useEffect, useCallback } from 'react';

const inputStyle = {
  padding: '7px 12px',
  borderRadius: 'var(--border-radius)',
  border: '1px solid var(--border-color)',
  background: 'var(--surface-color)',
  color: 'var(--text-primary)',
  fontSize: 13,
};

const card = {
  background: 'var(--card-bg)',
  border: '1px solid var(--border-color)',
  borderRadius: 'var(--border-radius)',
  padding: '10px 12px',
  marginBottom: 8,
  cursor: 'pointer',
  color: 'var(--text-primary)',
};

function btn() {
  return {
    padding: '7px 16px',
    borderRadius: 'var(--border-radius)',
    border: 'none',
    background: 'var(--primary-color)',
    color: 'var(--text-on-primary)',
    fontSize: 13,
    cursor: 'pointer',
    fontWeight: 600,
  };
}

export default function AdminCorneliusGraph() {
  const [userId, setUserId] = useState('13');
  const [q, setQ] = useState('');
  const [hops, setHops] = useState(2);
  const [entities, setEntities] = useState([]);
  const [selected, setSelected] = useState(null);
  const [walk, setWalk] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const loadEntities = useCallback(async () => {
    if (!userId.trim()) return;
    setLoading(true);
    setError('');
    try {
      const url = `/api/opo/cornelius-memories/graph/entities?user_id=${encodeURIComponent(userId.trim())}` + (q ? `&q=${encodeURIComponent(q)}` : '');
      const r = await fetch(url);
      const d = await r.json();
      if (!r.ok || d.success === false) throw new Error(d.error || `HTTP ${r.status}`);
      setEntities(d.entities || []);
    } catch (e) {
      setError(e.message);
      setEntities([]);
    }
    setLoading(false);
  }, [userId, q]);

  useEffect(() => { loadEntities(); }, [loadEntities]);

  const pickEntity = useCallback(async (e) => {
    setSelected(e);
    setWalk(null);
    try {
      const r = await fetch(`/api/opo/cornelius-memories/graph/entities/${e.id}/walk?user_id=${encodeURIComponent(userId.trim())}&hops=${hops}`);
      const d = await r.json();
      if (!r.ok || d.success === false) throw new Error(d.error || `HTTP ${r.status}`);
      setWalk(d);
    } catch (err) {
      setError(err.message);
    }
  }, [userId, hops]);

  return (
    <div style={{ maxWidth: 1100 }}>
      <div style={{ marginBottom: 14, color: 'var(--text-secondary)', fontSize: 13 }}>
        Entity graph for memory rows. Click an entity to walk relations and gather connected memories.
      </div>
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={userId} onChange={(e) => setUserId(e.target.value)} placeholder="User ID" style={{ ...inputStyle, width: 140 }} />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Filter entities" style={{ ...inputStyle, width: 220 }} />
        <select value={hops} onChange={(e) => setHops(parseInt(e.target.value, 10))} style={{ ...inputStyle, cursor: 'pointer' }}>
          {[1, 2, 3].map(h => <option key={h} value={h}>{h} hop{h > 1 ? 's' : ''}</option>)}
        </select>
        <button onClick={loadEntities} style={btn()}>Refresh</button>
      </div>

      {error && <div style={{ color: 'var(--danger-color)', marginBottom: 12, fontSize: 13 }}>{error}</div>}

      <div style={{ display: 'grid', gridTemplateColumns: '320px 1fr', gap: 16 }}>
        <div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>
            Entities ({entities.length})
          </div>
          {loading && <div style={{ color: 'var(--text-secondary)' }}>Loading…</div>}
          {!loading && entities.map((e) => (
            <div
              key={e.id}
              onClick={() => pickEntity(e)}
              style={{
                ...card,
                background: selected?.id === e.id ? 'rgba(99,102,241,0.12)' : card.background,
              }}
            >
              <div style={{ fontSize: 12, fontWeight: 600 }}>{e.display_name}</div>
              <div style={{ fontSize: 10, color: 'var(--text-secondary)', marginTop: 2 }}>
                {e.entity_type} • mention {e.mention_count}
              </div>
            </div>
          ))}
        </div>

        <div>
          {!selected && (
            <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>Pick an entity to walk.</div>
          )}
          {selected && walk && (
            <>
              <div style={{ ...card, cursor: 'default', marginBottom: 16 }}>
                <strong>{selected.display_name}</strong>
                <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 4 }}>
                  Memories within {walk.hops} hop(s): <strong>{walk.memories?.length || 0}</strong>
                  {' '}• Relations: <strong>{walk.relations?.length || 0}</strong>
                </div>
              </div>

              {walk.relations && walk.relations.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>Relations</div>
                  {walk.relations.map((r, i) => (
                    <div key={i} style={{ ...card, cursor: 'default' }}>
                      <code style={{ fontSize: 11 }}>{r.from_label}</code>
                      <span style={{ color: 'var(--text-secondary)', fontSize: 11, margin: '0 6px' }}>
                        →{r.relation_type} ({Number(r.confidence).toFixed(2)})→
                      </span>
                      <code style={{ fontSize: 11 }}>{r.to_label}</code>
                    </div>
                  ))}
                </div>
              )}

              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6 }}>Memories</div>
              {(walk.memories || []).map((m) => (
                <div key={m.memory_id} style={{ ...card, cursor: 'default' }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <span style={{
                      fontSize: 10,
                      padding: '2px 6px',
                      borderRadius: 6,
                      background: 'var(--surface-color)',
                      border: '1px solid var(--border-color)',
                    }}>hop {m.hop_distance}</span>
                    <code style={{ fontSize: 11 }}>{m.memory_type}</code>
                    <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
                      importance {m.importance ?? '—'} • mem #{m.memory_id}
                    </span>
                  </div>
                  <div style={{ fontSize: 12, marginTop: 6, color: 'var(--text-primary)' }}>
                    {(m.content || '').slice(0, 280)}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
